import { runtimeBus } from '../../app/runtimeBus';
import { ProgressController } from '../../app/ProgressController';
import { RunRewardController } from '../../app/RunRewardController';
import { COSMETICS, getCosmetic, INITIAL_COSMETICS, kindLabel, RARITIES, type CosmeticKind, type Rarity } from '../../game/content/cosmetics';
import { RARITY_WEIGHTS, type PullCount } from '../../game/gacha/gachaRules';
import { ProgressStore, type ClearReceipt, type DrawReceipt, type Receipt } from '../../game/storage/progressStore';
import type { SessionState } from '../../game/simulation/types';
import { formatRunTime } from '../../game/ranking/types';
import { ModalCoordinator } from '../ModalCoordinator';
import { BoardSkinPresenter } from '../skins/BoardSkinPresenter';
import { GachaPresentationController } from './GachaPresentationController';
import { escapeHtml, itemCard, previewMarkup } from './cosmeticMarkup';
import { RunRewardsUI } from './RunRewardsUI';

type View = 'home' | 'skins' | 'rates' | 'result' | 'reward' | 'preview';
const number = (value: number): string => value.toLocaleString('ja-JP');

export class EconomyUI {
  private readonly dialog = document.createElement('dialog');
  private readonly content: HTMLElement;
  private readonly coordinator: ModalCoordinator;
  private readonly board: BoardSkinPresenter;
  private readonly presentation = new GachaPresentationController();
  private progress!: ProgressController;
  private readonly ready: Promise<void>;
  private view: View = 'home';
  private previousView: View = 'skins';
  private lastState: SessionState | null = null;
  private currentReceipt: Receipt | null = null;
  private lastDraw: DrawReceipt | null = null;
  private readonly pendingClears = new Set<string>();
  private activeClearId: string | null = null;
  private previewId = 'frame-default';
  private kind: CosmeticKind = 'board-frame';
  private rarity: Rarity | 'all' = 'all';
  private error = '';
  private busy = false;
  private animationGeneration = 0;
  private skipLoading: (() => void) | null = null;
  private readonly unsubscribeState: () => void;
  private readonly runRewards: RunRewardController;
  private readonly rewardsUI: RunRewardsUI;

  constructor(private readonly root: HTMLElement, onModalChange: (open: boolean) => void) {
    this.dialog.className = 'economy-dialog';
    this.dialog.id = 'economy-dialog';
    this.dialog.setAttribute('aria-labelledby', 'economy-title');
    this.dialog.innerHTML = `<div class="economy-shell"><header class="economy-header"><div><span class="economy-eyebrow">OBSERVATION</span><h2 id="economy-title">盤面の観測</h2></div><div class="economy-header__actions"><span class="observation-wallet"><span class="observation-stone-icon" aria-hidden="true">◆</span><span>観測石</span><strong data-stone-balance>—</strong></span><button class="economy-button economy-close" type="button" data-action="close" aria-label="閉じる">×</button></div></header><div class="economy-content"></div><p class="economy-error" role="alert" hidden></p></div>`;
    document.body.append(this.dialog);
    this.content = this.dialog.querySelector('.economy-content')!;
    this.coordinator = new ModalCoordinator(this.dialog, onModalChange);
    this.board = new BoardSkinPresenter(root.querySelector('.playfield-frame')!);
    const wallet = document.createElement('button');
    wallet.className = 'observation-wallet observation-wallet--hud';
    wallet.type = 'button'; wallet.setAttribute('aria-label', '観測石とガチャ');
    wallet.innerHTML = '<span class="observation-stone-icon" aria-hidden="true">◆</span><span>観測石</span><strong data-stone-balance>—</strong><small data-unsaved-clear hidden>報酬未保存</small>';
    wallet.addEventListener('click', () => { void this.open('home'); });
    root.querySelector('.brand-actions')?.prepend(wallet);
    for (const [id, view] of [['gacha-button', 'home'], ['skin-button', 'skins']] as const) {
      const button = root.querySelector<HTMLButtonElement>(`#${id}`)!;
      button.addEventListener('click', () => { void this.open(view); });
    }
    this.dialog.addEventListener('click', event => { void this.handleClick(event); });
    this.dialog.addEventListener('error', event => {
      if (event.target instanceof HTMLImageElement) event.target.hidden = true;
    }, true);
    this.dialog.addEventListener('change', event => {
      const select = event.target as HTMLSelectElement;
      if (select.matches('[data-rarity-filter]')) { this.rarity = select.value as Rarity | 'all'; this.render(); }
    });
    this.dialog.addEventListener('cancel', event => {
      event.preventDefault();
      if (this.busy) this.skipPresentation();
      else if (!this.busy) void this.close();
    });
    this.dialog.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      if (this.busy) this.skipPresentation();
      else if (!this.busy) void this.close();
    });
    this.runRewards = new RunRewardController(async () => { await this.ready; return this.progress; });
    this.rewardsUI = new RunRewardsUI(root, this.dialog, this.runRewards, () => { void this.open('home'); }, async () => {
      await this.runRewards.retry();
      for (const id of this.pendingClears) await this.saveClear(id);
    });
    this.unsubscribeState = runtimeBus.onState((state, events) => {
      this.lastState = state;
      this.rewardsUI.updateState(state);
      this.runRewards.observe(state, events);
      for (const button of root.querySelectorAll<HTMLButtonElement>('#gacha-button, #skin-button, .observation-wallet--hud')) button.disabled = state.status === 'presenting';
      for (const event of events) if (event.type === 'run-cleared' && !this.pendingClears.has(event.runId)) {
        this.pendingClears.add(event.runId);
        this.activeClearId = event.runId;
        this.runRewards.clearPending(event.runId);
        this.coordinator.reserve();
        void this.saveClear(event.runId);
      }
      if (this.activeClearId && state.status !== 'presenting' && !this.coordinator.isOpen) {
        this.view = 'reward'; this.coordinator.open(); this.render();
      }
    });
    this.ready = this.initialize();
  }

  private async initialize(): Promise<void> {
    let store = new ProgressStore();
    if (import.meta.env.DEV) {
      const { developmentProgressStore } = await import('../../debug/gachaDebug');
      store = developmentProgressStore() ?? store;
    }
    this.progress = new ProgressController(store);
    this.progress.onChange(profile => {
      for (const balance of document.querySelectorAll<HTMLElement>('[data-stone-balance]')) balance.textContent = number(profile.stones);
      this.board.update(profile);
      if (!this.busy && this.coordinator.isOpen && this.view !== 'preview') this.render();
    });
    try {
      await this.progress.refresh();
      const pending = await store.pendingReceipts();
      if (pending.length && !this.activeClearId) this.showReceipt(pending[0]!);
    } catch (error) { this.setError(error); }
  }

  private async open(view: View): Promise<void> {
    if (this.lastState?.status === 'presenting' || this.busy) return;
    runtimeBus.gachaAudio?.unlock();
    await this.ready;
    this.error = '';
    const pendingClear = this.pendingClears.values().next().value;
    if (pendingClear) { this.activeClearId = pendingClear; this.view = 'reward'; this.error = 'クリア報酬はまだ保存されていません。保存を再試行してください。'; }
    else this.view = view;
    this.coordinator.open(); this.render();
    void runtimeBus.gachaAudio?.prepare();
    void this.progress.refresh().catch(error => this.setError(error));
  }

  private async saveClear(id: string): Promise<void> {
    this.runRewards.clearPending(id);
    await this.ready;
    try {
      const receipt = await this.progress.awardClear(id);
      this.pendingClears.delete(id);
      this.root.querySelector<HTMLElement>('[data-unsaved-clear]')!.hidden = this.pendingClears.size === 0;
      this.runRewards.clearSaved(receipt);
      if (this.activeClearId === id) {
        this.currentReceipt = receipt;
        if (this.lastState?.status !== 'presenting') this.showReceipt(receipt);
      }
    } catch (error) {
      this.runRewards.clearFailed(id);
      this.root.querySelector<HTMLElement>('[data-unsaved-clear]')!.hidden = false;
      this.setError(error); if (this.view === 'reward') this.render();
    }
  }

  private showReceipt(receipt: Receipt): void {
    this.currentReceipt = receipt;
    if (receipt.kind === 'draw') { this.lastDraw = receipt; this.view = 'result'; }
    else {
      this.activeClearId = receipt.id; this.view = 'reward';
      this.runRewards.clearSaved(receipt);
      void this.runRewards.load(receipt.id);
    }
    this.coordinator.open(); this.render();
  }

  private async acknowledge(): Promise<void> {
    const receipt = this.currentReceipt;
    if (!receipt) return;
    await this.progress.acknowledge(receipt);
    this.currentReceipt = null;
    if (receipt.kind === 'clear') this.activeClearId = null;
  }

  private async close(): Promise<void> {
    if (this.busy) return;
    if (this.activeClearId && !this.currentReceipt) {
      this.activeClearId = null;
      this.coordinator.close();
      return;
    }
    try {
      await this.acknowledge();
      const pending = await this.progress.store.pendingReceipts();
      if (pending.length) { this.showReceipt(pending[0]!); return; }
      this.presentation.skip(); runtimeBus.gachaAudio?.stop();
      this.coordinator.close();
    } catch (error) { this.setError(error); }
  }

  private async handleClick(event: MouseEvent): Promise<void> {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!button || button.disabled) return;
    if (button.dataset.action === 'skip') { this.skipPresentation(); return; }
    if (this.busy) return;
    this.error = ''; this.showError();
    try {
      if (button.dataset.preview) {
        this.previousView = this.view;
        this.previewId = button.dataset.preview;
        this.view = 'preview'; this.render(); return;
      }
      if (button.dataset.kind) { this.kind = button.dataset.kind as CosmeticKind; this.render(); return; }
      if (button.dataset.pull) { await this.draw(Number(button.dataset.pull) as PullCount); return; }
      switch (button.dataset.action) {
        case 'close': case 'continue': await this.close(); break;
        case 'ranking':
          await this.close();
          if (!this.coordinator.isOpen) this.root.querySelector<HTMLButtonElement>('#ranking-button')?.click();
          break;
        case 'rates': this.view = 'rates'; this.render(); break;
        case 'home': await this.acknowledge(); this.view = 'home'; this.render(); void runtimeBus.gachaAudio?.prepare(); break;
        case 'skins': this.view = 'skins'; this.render(); break;
        case 'back': this.view = this.previousView; this.render(); break;
        case 'retry': await this.progress.refresh(); this.render(); break;
        case 'retry-clear': if (this.activeClearId) await this.saveClear(this.activeClearId); break;
        case 'equip': {
          const item = getCosmetic(this.previewId);
          if (item) { await this.progress.equip(item.kind, item.id); this.render(); }
          break;
        }
        case 'reset-skin': await this.progress.equip(this.kind, this.kind === 'board-frame' ? 'frame-default' : 'background-default'); this.render(); break;
        case 'restart':
          await this.acknowledge(); this.activeClearId = null;
          this.coordinator.close(); runtimeBus.sendUi({ type: 'restart' }); break;
      }
    } catch (error) { this.setError(error); }
  }

  private async draw(count: PullCount): Promise<void> {
    this.busy = true; this.setCloseDisabled(true);
    this.dialog.dataset.state = 'saving';
    this.dialog.querySelector('#economy-title')!.textContent = '盤面を観測中';
    for (const button of this.content.querySelectorAll<HTMLButtonElement>('button')) button.disabled = true;
    const generation = ++this.animationGeneration;
    runtimeBus.gachaAudio?.unlock();
    try {
      await this.acknowledge();
      const receipt = await this.progress.draw(crypto.randomUUID(), count);
      this.currentReceipt = receipt; this.lastDraw = receipt;
      this.dialog.dataset.state = 'loading';
      this.content.innerHTML = '<div class="observation-loading" role="status"><span class="observation-loading__ring"></span><p>新しい盤面を呼び集めています…</p><button class="economy-button" data-action="skip">結果をすぐに見る</button></div>';
      this.content.querySelector<HTMLButtonElement>('button')!.focus({ preventScroll: true });
      const paths = [...new Set(receipt.pulls.map(p => getCosmetic(p.itemId)?.image).filter((x): x is string => Boolean(x)))];
      let timeout: ReturnType<typeof setTimeout> | undefined;
      let skipped = false;
      await Promise.race([
        Promise.allSettled([...paths.map(path => { const image = new Image(); image.src = path; return image.decode(); }), runtimeBus.gachaAudio?.prepare() ?? Promise.resolve()]),
        new Promise<void>(resolve => { timeout = setTimeout(resolve, 3000); }),
        new Promise<void>(resolve => { this.skipLoading = () => { skipped = true; resolve(); }; }),
      ]);
      clearTimeout(timeout);
      this.skipLoading = null;
      if (generation !== this.animationGeneration) return;
      this.dialog.dataset.state = 'animating';
      if (!skipped && !document.hidden) await this.presentation.play(this.content, receipt);
      this.view = 'result';
    } catch (error) { this.setError(error); await this.progress.refresh().catch(() => {}); }
    finally { this.skipLoading = null; this.busy = false; this.setCloseDisabled(false); this.render(); }
  }

  private skipPresentation(): void { if (this.skipLoading) this.skipLoading(); else this.presentation.skip(); }

  private render(): void {
    if (this.busy) return;
    this.dialog.dataset.state = this.view;
    const titles: Record<View, string> = { home: '盤面の観測', skins: '盤面を装う', rates: '排出内容・確率', result: '観測した盤面', reward: '観測者への到達', preview: '盤面の試着' };
    this.dialog.querySelector('#economy-title')!.textContent = titles[this.view];
    const p = this.progress?.profile ?? null;
    if (this.view === 'home') {
      const balance = p?.stones ?? 0;
      this.content.innerHTML = `<section class="observation-home"><div class="observation-home__copy"><span class="economy-eyebrow">まだ見ぬ、盤面へ。</span><h3>その石に眠る<br/>新しい世界を、ひらく。</h3><p>観測石から現れる、フレームと盤面内背景。<br/>出会った意匠を重ねて、あなただけの盤面に。</p><button class="economy-text-button" type="button" data-action="rates">全${COSMETICS.length}種の排出内容・確率 <span aria-hidden="true">↗</span></button></div>
        <div class="observation-home__altar" aria-hidden="true"><div class="idle-orbit"></div><img src="/assets/gacha/fx/observation-stone.webp" alt=""/></div>
        <div class="observation-actions">${([1,10] as const).map(count => `<button class="observation-pull ${count===10?'observation-pull--ten':''}" type="button" data-pull="${count}" ${!p || balance<count*100?'disabled':''}><span>${count===1?'1回引く':'10回引く'}</span><strong><span aria-hidden="true">◆</span> ${number(count*100)}</strong>${balance<count*100?`<small>あと${number(count*100-balance)}個</small>`:'<small>観測石を使う</small>'}</button>`).join('')}</div>
        <p class="observation-home__footnote">第二共鳴から、到達するたびに観測石を獲得。途中で負けても残ります。<br/>五共鳴のモードは合計1,000〜1,500個、三共鳴は300〜450個。クリアでさらに100〜9,000個。</p>
      </section>${this.lastDraw ? `<section class="recent-observation"><h3>前回の観測</h3><div>${this.lastDraw.pulls.slice(0,5).map(pull=>{ const item=getCosmetic(pull.itemId);return item?itemCard(item,p,pull):''; }).join('')}</div></section>`:''}${!p?'<button class="economy-button" data-action="retry">保存データを再読込</button>':''}`;
    } else if (this.view === 'skins') {
      const list = [...INITIAL_COSMETICS, ...COSMETICS].filter(item => item.kind === this.kind && (this.rarity === 'all' || (!item.initial && item.rarity === this.rarity)));
      this.content.innerHTML = `<div class="skin-toolbar"><div class="skin-tabs" role="group" aria-label="スキンの種類">${(['board-frame','board-background'] as const).map(kind=>`<button class="economy-button" data-kind="${kind}" aria-pressed="${kind===this.kind}">${kindLabel(kind)}</button>`).join('')}</div><label>レアリティ <select data-rarity-filter>${['all',...RARITIES].map(r=>`<option value="${r}" ${r===this.rarity?'selected':''}>${r==='all'?'すべて':r}</option>`).join('')}</select></label><button class="economy-text-button" data-action="reset-skin">初期に戻す</button></div><p class="economy-note">所持 ${COSMETICS.filter(item=>item.kind===this.kind && (p?.ownedCounts[item.id]??0)>0).length} / ${COSMETICS.filter(item=>item.kind===this.kind).length}　未所持のスキンも試着できます。</p><div class="cosmetic-grid">${list.map(item=>itemCard(item,p)).join('')}</div>`;
    } else if (this.view === 'rates') {
      this.content.innerHTML = `<p class="economy-note">フレーム${COSMETICS.filter(item=>item.kind==='board-frame').length}種と盤面内背景${COSMETICS.filter(item=>item.kind==='board-background').length}種が同じガチャから出現します。同じレアリティ内の各アイテムは同じ確率です。</p><div class="rate-summary">${RARITIES.map(r=>`<div><strong>${r}</strong><span>${(RARITY_WEIGHTS[r]/100).toFixed(2)}%</span></div>`).join('')}</div><p class="economy-note">10連は独立した抽選10回です。確定枠・天井はありません。所持済みも同じ確率で出現し、所持数に加算されます。</p>${[...RARITIES].reverse().map(r=>`<section class="rate-group"><h3>${r}<small>各アイテム ${RARITY_WEIGHTS[r]/(100*COSMETICS.filter(item=>item.rarity===r).length)}%</small></h3><div class="cosmetic-grid">${COSMETICS.filter(item=>item.rarity===r).map(item=>itemCard(item,p)).join('')}</div></section>`).join('')}<button class="economy-button" data-action="home">ガチャへ戻る</button>`;
    } else if (this.view === 'result' && this.lastDraw) {
      const receipt = this.lastDraw;
      this.content.innerHTML = `<div class="observation-result-heading"><span class="economy-eyebrow">OBSERVATION COMPLETE</span><h3>新しい景色を、あなたの盤面に。</h3><p>初獲得 ${receipt.pulls.filter(pull=>pull.isNew).length}種 <span>／ 観測石 ${number(receipt.cost)}個を使用</span></p></div><div class="cosmetic-grid cosmetic-grid--results" aria-label="ガチャ結果">${receipt.pulls.map(pull=>{const item=getCosmetic(pull.itemId);return item?itemCard(item,p,pull):`<div class="cosmetic-card">${escapeHtml(pull.itemId)}<span>獲得済み</span></div>`;}).join('')}</div><div class="economy-footer"><button class="economy-button" data-action="close">閉じる</button><button class="economy-button economy-button--primary" data-pull="${receipt.count}" ${(p?.stones??0)<receipt.cost?'disabled':''}>もう${receipt.count===1?'1回':'10回'}引く　◆ ${number(receipt.cost)}</button></div>`;
    } else if (this.view === 'reward') {
      const receipt = this.currentReceipt?.kind === 'clear' ? this.currentReceipt : null;
      this.content.innerHTML = `<section class="clear-reward"><span class="economy-eyebrow">RESONANCE COMPLETE</span><h3>盤理の観測者へ、到達。</h3><img class="clear-reward__observer" src="/assets/characters/board-logic-observer.webp" alt="盤理の観測者"/><p>幾重もの共鳴が、新しい世界をひらきました。</p><div class="clear-reward__amount"><span>クリア報酬・観測石</span><strong>${receipt?`+${number(receipt.amount)}`:this.error?'未保存':'保存中…'}</strong></div><p class="economy-note">このプレイのクリア報酬は1回です。続けてスコアを伸ばすこともできます。</p><div class="economy-footer"><button class="economy-button economy-button--primary" data-action="home" ${!receipt?'disabled':''}>ガチャを引く</button><button class="economy-button" data-action="continue" ${!receipt&&!this.error?'disabled':''}>続けて遊ぶ</button><button class="economy-text-button" data-action="restart" ${!receipt?'disabled':''}>もう一度遊ぶ</button>${!receipt&&this.error?'<button class="economy-button" data-action="retry-clear">報酬の保存を再試行</button>':''}</div></section>`;
      if (this.lastState?.clearedThisRun && this.activeClearId === this.lastState.runId && this.lastState.clearTimeMs !== null) {
        this.content.querySelector('.clear-reward__amount')?.insertAdjacentHTML('beforebegin', `<div class="ranking-clear-time"><span>CLEAR TIME</span><strong>${formatRunTime(this.lastState.clearTimeMs)}</strong><small>盤理の観測者への初到達</small></div>`);
      }
      this.content.querySelector('.economy-footer')?.insertAdjacentHTML('beforeend', `<button class="economy-button" data-action="ranking" ${!receipt ? 'disabled' : ''}>ランキングを見る</button>`);
      if (receipt) {
        const summary = document.createElement('p'); summary.className = 'clear-reward__summary';
        summary.dataset.clearRunSummary = ''; summary.dataset.runId = receipt.id;
        this.content.querySelector('.clear-reward__amount')?.after(summary);
      }
    } else if (this.view === 'preview') {
      const item = getCosmetic(this.previewId)!;
      const owned = (p?.ownedCounts[item.id]??0)>0;
      const equipped = p?.frameId===item.id || p?.backgroundId===item.id;
      const frame = item.kind==='board-frame'?item.id:p?.frameId??'frame-default';
      const background = item.kind==='board-background'?item.id:p?.backgroundId??'background-default';
      this.content.innerHTML = `<section class="skin-fitting"><div class="skin-fitting__board">${previewMarkup(frame,background,true)}</div><div class="skin-fitting__details"><span class="economy-eyebrow">${kindLabel(item.kind)} · ${item.initial?'初期':item.rarity}</span><h3>${escapeHtml(item.name)}</h3>${item.projection?`<p class="skin-fitting__character">${escapeHtml(item.projection.character)}の姿を映した盤面</p>`:''}<p>いまの装備と組み合わせた盤面です。<br/>盤面の広さや石の動きは変わりません。</p><button class="economy-button economy-button--primary" data-action="equip" ${!owned||equipped?'disabled':''}>${equipped?'装備中':owned?'装備する':'未所持'}</button><button class="economy-text-button" data-action="back">一覧へ戻る</button></div></section>`;
    }
    this.showError();
    this.rewardsUI?.render();
    if (this.coordinator.isOpen && !this.dialog.contains(document.activeElement)) {
      this.dialog.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
    }
  }

  private setCloseDisabled(value: boolean): void { this.dialog.querySelector<HTMLButtonElement>('[data-action="close"]')!.disabled = value; }
  private setError(error: unknown): void { this.error = error instanceof Error ? error.message : '操作を完了できませんでした。再試行してください。'; this.showError(); }
  private showError(): void { const element=this.dialog.querySelector<HTMLElement>('.economy-error')!;element.textContent=this.error;element.hidden=!this.error; }
  destroy(): void { this.animationGeneration++; this.presentation.skip(); this.unsubscribeState(); this.rewardsUI.destroy(); this.runRewards.destroy(); this.progress?.destroy(); this.coordinator.close(); this.dialog.remove(); }
}
